import React from 'react';
import { Stack, Text, Button, useBreakpoint } from '@moda/om';
import { hrefFor } from '../../routers';
import { formatPrice } from '../../lib/formatPrice';
import { useCart, useCartItems } from '../../hooks/useCart';
import { AmazonPayButton, PayPalButton } from '../PaymentSourceButton';
import { AffirmPromoMessage } from '../AffirmPromoMessage';
import { RewardSummary } from '../Rewards';
import { PaymentSummaryRow } from './PaymentSummaryRow';

import './ShoppingBagPaymentSummary.scss';

export const ShoppingBagPaymentSummary: React.FC = () => {
  const {
    cart: {
      attributes: {
        paymentSummary: {
          subtotal,
          total,
          estimatedTaxes,
          estimatedShipping,
          siteCredit,
          promotionDiscount,
          payToday,
          payLater,
          taxType,
          isShippingPromotionApplied
        },
        rewardSummary
      }
    }
  } = useCart();
  const { unavailableItems, invalidItems, personalizationRequiredItems } = useCartItems();
  const { matches: isDesktop } = useBreakpoint({ gt: 'md' });

  return (
    <Stack space={4} className="ShoppingBagPaymentSummary">
      <Text treatment="h5" family="serif">
        Payment Summary
      </Text>
      <Stack space={3}>
        <PaymentSummaryRow
          label="Order Subtotal"
          value={formatPrice(subtotal.currency, subtotal.price)}
        />
        <PaymentSummaryRow
          label={taxType}
          tooltipAnchor={isDesktop ? 'bottomCenter' : 'bottomLeft'}
          tooltip={
            <>
              Duties and taxes will be calculated at checkout, where applicable. Learn more about
              Duties and Taxes{' '}
              <a
                className="ShoppingBagPaymentSummary__faq-link"
                href="https://help.modaoperandi.com/hc/en-us/articles/211521823-Taxes-Duties"
                rel="noopener noreferrer"
                target="_blank"
              >
                in our FAQs
              </a>
              .
            </>
          }
          value={formatPrice(estimatedTaxes.currency, estimatedTaxes.price)}
        />
        <PaymentSummaryRow
          label="Est. Shipping"
          tooltipAnchor={isDesktop ? 'bottomCenter' : 'bottomLeft'}
          tooltip="We estimate shipping fees using your location. You will be able to change your shipping method before completing your order."
          value={
            isShippingPromotionApplied
              ? 'Free'
              : formatPrice(estimatedShipping.currency, estimatedShipping.price)
          }
        />
      </Stack>
      <div className="ShoppingBagPaymentSummary__total">
        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, total.price)} />
      </div>
      <Stack space={3}>
        {promotionDiscount.price > 0 && (
          <PaymentSummaryRow
            label="Gift Code"
            value={`-${formatPrice(promotionDiscount.currency, promotionDiscount.price)}`}
          />
        )}
        {siteCredit.price > 0 && (
          <PaymentSummaryRow
            label="Site Credit"
            value={`-${formatPrice(siteCredit.currency, siteCredit.price)}`}
          />
        )}
        <Stack space={1} className="ShoppingBagPaymentSummary__pay-today">
          {rewardSummary?.enrolledForLoyalty && <RewardSummary theme="simple" />}
          <div className="ShoppingBagPaymentSummary__pay-today" data-testid="Pay Today">
            <PaymentSummaryRow
              label="Est. Pay Today"
              value={formatPrice(payToday.currency, payToday.price)}
            />
            {Number(payLater.price) === 0 && (
              <div className="ShoppingBagPaymentSummary__affirm-promo">
                <AffirmPromoMessage pageType="cart" price={Number(payToday.price)} />
              </div>
            )}
          </div>
        </Stack>
        <PaymentSummaryRow
          label="Est. Remaining Balance"
          tooltip="Preorder items are made especially for you and require a 50% deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
          value={formatPrice(payLater.currency, payLater.price)}
        />
      </Stack>
      <Button
        to={hrefFor.CheckoutPage()}
        disabled={
          unavailableItems.length > 0 ||
          invalidItems.length > 0 ||
          personalizationRequiredItems.length > 0
        }
        data-testid="shoppingBagCheckoutButton"
      >
        Checkout
      </Button>
      <div className="ShoppingBagPaymentSummary__divider">
        <Text className="ShoppingBagPaymentSummary__divider-text">OR</Text>
      </div>
      <Text className="ShoppingBagPaymentSummary__label">
        Skip the forms and use your saved information
      </Text>
      <div className="ShoppingBagPaymentSummary__payment-methods">
        <PayPalButton />
        <AmazonPayButton />
      </div>
    </Stack>
  );
};
